/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Table component for reports shedule
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useEffect, useMemo } from 'react';
import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectPageReportsConstructorTranslation } from '@modules/internationalization/intSelectors';
import { initialise, Table, useTableListApi, useTableMetaApi } from '@modules/table';
import { tableIsErrorSelector, tableIsLoadingSelector } from '@modules/table/selectors';

import { EConstructorItem } from '../../constants';
import { reportsConstructorCurrentRowItemSelector } from '../../selector';
import { ReportId } from '../../types';
import { setTableScheduleConfig } from '../../utils/tableConstructorSchedule';
import { tableScheduleCellConfig } from './TableScheduleCells';
import { TableScheduleColumns } from './TableScheduleColumns';

export const TableSchedule: FC = () => {
    const dispatch = useDispatch();

    const tReportsConstructor = useSelector(makeSelectPageReportsConstructorTranslation());
    const currentRowItem = useSelector(reportsConstructorCurrentRowItemSelector);
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);

    const isTemplate = currentRowItem?.rowType === EConstructorItem.TEMPLATE;

    const columnsConfig = TableScheduleColumns(tReportsConstructor);

    const initialiseConfig = useMemo(() =>
        setTableScheduleConfig(ReportId(currentRowItem?.rowItemId ?? 0), isTemplate),
        [currentRowItem?.rowItemId, isTemplate]);

    const { columns } = useTableMetaApi(columnsConfig, tableScheduleCellConfig);
    const { data, infiniteLoader, isCompact } = useTableListApi();

    useEffect(() => {
        if (initialiseConfig) {
            dispatch(initialise(initialiseConfig));
        }
    }, [dispatch, initialiseConfig]);

    /**
     * Пока для шаблонов не рисуем таблицу, ждём бек
     */

    return (
        !!currentRowItem && !!columns.length && !isTemplate ? <Table
            columns={columns}
            data={data}
            dataName='reports-schedule-table'
            dataNameRow='report.def'
            infiniteScroll={infiniteLoader}
            isCompact={isCompact}
            isError={isError}
            isInnerTable={true}
            isLoading={isLoading}
            isRowHeightAuto={true}
        /> : null
    );
};
